import { createSlice } from "@reduxjs/toolkit";

// UI-relevant call state only. The RTCPeerConnection itself is not
// serializable and lives in useCallConnection's refs, not here — this slice
// just tracks what the banner/bar/button need to render.
const initialState = {
  incoming: null, // { callId, wacid, from, offerSdp } — whichever ringing call currently occupies the corner widget
  active: null, // { callId, wacid, direction, status, contactId, remoteNumber, connectedAt }
  error: null,
  // callIds this tab actually received call:incoming for (has an offerSdp
  // for, in useCallConnection's pendingIncomingCallsRef) — NOT every call
  // visible in the workspace-wide Calls list. In ring-group mode an agent
  // outside the group sees a call ringing there but was never sent its
  // offer, so it can't be answered from this tab; this is what the Calls
  // sidebar checks before showing Accept/Reject on a card.
  answerableCallIds: [],
  // A transfer offered TO this agent — separate from `incoming` (a brand new
  // ring) since the two can never legitimately collide (an agent eligible to
  // receive a transfer is, by the live-agents picker's own filtering, never
  // already on a call) but they mean different things and need different
  // actions (accept/decline a transfer vs. answer/reject a call).
  incomingTransfer: null, // { callId, wacid, fromAgentName, contactId, contactName, waNumber }
};

const callSlice = createSlice({
  name: "calls",
  initialState,
  reducers: {
    callIncoming(state, action) {
      // Only one ring occupies the widget slot at a time — a second
      // simultaneous call still rings (and is answerable/rejectable from the
      // Calls sidebar, tracked separately in liveCallsSlice + this hook's own
      // pending-calls map), it just doesn't take over the corner widget.
      if (state.incoming || state.active) return;
      state.incoming = action.payload;
    },
    // Explicit override of which ringing call the widget shows — dispatched
    // when an agent clicks a *different* ringing call's card in the Calls
    // sidebar than whichever one auto-won the widget slot. Unlike
    // callIncoming, unconditional: this is a deliberate user choice, not a
    // race between simultaneous rings.
    focusIncomingCall(state, action) {
      state.incoming = action.payload;
    },
    // callId is required (not optional) so resolving one ringing call never
    // clears the widget's display of a *different* still-ringing call — the
    // bug this shape specifically prevents.
    clearIncoming(state, action) {
      if (state.incoming?.callId === action.payload) state.incoming = null;
    },
    callStarted(state, action) {
      // Only clear the widget's ringing display if the call being started IS
      // the one it's showing — answering a second call from the Calls
      // sidebar must not blank out an unrelated call still ringing in the widget.
      if (state.incoming?.callId === action.payload.callId) state.incoming = null;
      state.active = { status: "connecting", ...action.payload };
      state.error = null;
    },
    // Optional payload.connectedAt lets a caller supply the call's real,
    // already-known connect time (e.g. joinLiveCall joining a sfu-auto call
    // that connected minutes earlier, while it was ringing with no agent yet)
    // instead of defaulting to "now" — which would restart the widget's
    // elapsed-time display from zero at join time instead of showing how
    // long the call has actually been live, unlike every other call path.
    callConnected(state, action) {
      if (state.active) {
        state.active.status = "connected";
        // payload.connectedAt comes from the server (a JSON-serialized Mongo
        // date, i.e. an ISO string) — normalize to epoch ms, since CallWidget's
        // timer does plain arithmetic (Date.now() - connectedAt) against this.
        const suppliedMs = action.payload?.connectedAt ? new Date(action.payload.connectedAt).getTime() : NaN;
        state.active.connectedAt = Number.isFinite(suppliedMs) ? suppliedMs : Date.now();
      }
    },
    callStatusUpdate(state, action) {
      if (state.active && state.active.callId === action.payload.callId) {
        state.active.metaStatus = action.payload.metaStatus;
      }
    },
    // callId-scoped like clearIncoming — a ringing call elsewhere in the
    // workspace ending (missed, declined, claimed by someone else) must not
    // tear down THIS tab's own unrelated active call. Deliberately doesn't
    // touch state.incoming at all — clearIncoming is the single place that happens.
    callEnded(state, action) {
      if (!action.payload || state.active?.callId === action.payload) state.active = null;
    },
    callError(state, action) {
      state.error = action.payload;
    },
    clearCallError(state) {
      state.error = null;
    },
    markAnswerable(state, action) {
      if (!state.answerableCallIds.includes(action.payload)) state.answerableCallIds.push(action.payload);
    },
    unmarkAnswerable(state, action) {
      state.answerableCallIds = state.answerableCallIds.filter((id) => id !== action.payload);
    },
    // call:transferRequest — this agent specifically was picked as a transfer target.
    transferIncoming(state, action) {
      state.incomingTransfer = action.payload;
    },
    // callId-scoped like clearIncoming — the same reasoning applies: resolving
    // one transfer request must never blank out an unrelated one.
    clearIncomingTransfer(state, action) {
      if (state.incomingTransfer?.callId === action.payload) state.incomingTransfer = null;
    },
    // This tab just initiated a transfer on its own active call — swaps the
    // widget's hangup-only row for a "Transferring to X… [Cancel]" row.
    transferPendingStarted(state, action) {
      if (state.active?.callId === action.payload.callId) {
        state.active.transferPending = { toAgentId: action.payload.toAgentId, toAgentName: action.payload.toAgentName };
      }
    },
    // Transfer resolved one way or another (declined/cancelled/timed out) — back to normal connected display.
    transferPendingCleared(state, action) {
      if (state.active?.callId === action.payload) state.active.transferPending = null;
    },
  },
});

export const {
  callIncoming,
  focusIncomingCall,
  clearIncoming,
  callStarted,
  callConnected,
  callStatusUpdate,
  callEnded,
  callError,
  clearCallError,
  markAnswerable,
  unmarkAnswerable,
  transferIncoming,
  clearIncomingTransfer,
  transferPendingStarted,
  transferPendingCleared,
} = callSlice.actions;

export default callSlice.reducer;
